import { buildDigitalTwinDataUrl, getDigitalTwinDataHeaders } from "../../api/digitalTwin/fieldDataClient";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Snackbar from "@mui/material/Snackbar";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo } from "react";
import TimelineCard from "../../components/digitalTwin/detail/timelineCard.component";
import SingleInfoCard from "../../components/digitalTwin/detail/singleInfoCard.component";
import SuggestionCard from "../../components/digitalTwin/detail/suggestionCard.component";
import PlantInfosCard from "../../components/digitalTwin/detail/fieldInfosCard.component";
import MeasurementTrendCards from "../../components/digitalTwin/detail/measurementTrendCards.component";
import { useState } from "react";
import ThreeDCard from "../../components/digitalTwin/threeViewer/threeCard.component";
import { fetchPlantsMeasurementsStart } from "../../store/features/digitalTwin/plantsMeasurementsSlice";
import {
  createDigitalTwinModelObjectUrlFromBase64,
  formatDigitalTwinFeatureLabel,
  formatDigitalTwinMeasurementValue,
  getDigitalTwinModelBase64Payload,
  getHoursSinceTimestamp,
  getLatest3DMeshDigitalTwin,
  getLatestMeasurementByName,
} from "../../utils/digitalTwinUtils";
import { fetchPlantDigitalTwinStart } from "../../store/features/digitalTwin/plantDigitalTwinSlice";

const TREND_MEASUREMENTS = [
  { key: "ndvi", label: formatDigitalTwinFeatureLabel("ndvi") },
  { key: "volume", label: formatDigitalTwinFeatureLabel("volume") },
  { key: "area", label: formatDigitalTwinFeatureLabel("area") },
  { key: "count", label: formatDigitalTwinFeatureLabel("count") },
  { key: "height", label: formatDigitalTwinFeatureLabel("height") },
];

/* this is the detail of the selected plant */
const CropDetailPage = () => {
  const [open, setOpen] = useState(false);
  const [error] = useState("");
  const [modelUrl, setModelUrl] = useState(null);
  const [modelStatus, setModelStatus] = useState("idle");
  const [modelPayloadFormat, setModelPayloadFormat] = useState(null);
  const [openTrendKey, setOpenTrendKey] = useState(null);
  const dispatch = useDispatch();
  const plantsMeasurementsData = useSelector(
    (state) => state.plantsMeasurements?.data?.data,
  );
  const plantDigitalTwinData = useSelector((state) => state.plantDigitalTwin?.data);

  const selectedPlant = useSelector(
    (state) => state.selectedDigitalTwin.singlePlant,
  );

  /* We have a Snackbar dialog to show errors and the function handleClose close the dialog */
  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (selectedPlant) {
      setOpenTrendKey(null);
      console.log("plant", selectedPlant.id);
      const id = selectedPlant.id.toString();
      dispatch(
        fetchPlantsMeasurementsStart({
          plant_id: id,
        }),
      );
      dispatch(fetchPlantDigitalTwinStart({
        plant_id: id,
      }),
      );
    }
  }, [dispatch, selectedPlant]);

  const findNDVI = () => {
    const plantNDVI = getLatestMeasurementByName(plantsMeasurementsData, "ndvi");
    return formatDigitalTwinMeasurementValue(plantNDVI?.measurement_value, "ndvi");
  }

  const latest3DMeshDigitalTwin = useMemo(
    () => getLatest3DMeshDigitalTwin(plantDigitalTwinData),
    [plantDigitalTwinData],
  );
  const latest3DMeshUpdatedHoursAgo = useMemo(
    () => getHoursSinceTimestamp(latest3DMeshDigitalTwin?.survey_timestamp),
    [latest3DMeshDigitalTwin?.survey_timestamp],
  );

  useEffect(() => {
    if (
      !latest3DMeshDigitalTwin?.plant_id ||
      !latest3DMeshDigitalTwin?.survey_id ||
      !latest3DMeshDigitalTwin?.method_id
    ) {
      setModelUrl(null);
      setModelStatus("error");
      setModelPayloadFormat(null);
      return undefined;
    }

    let isMounted = true;
    let objectUrlToRevoke = null;

    const loadDigitalTwinModel = async () => {
      try {
        setModelStatus("loading");
        setModelUrl(null);
        setModelPayloadFormat(null);

        const params = new URLSearchParams({
          plant_id: String(latest3DMeshDigitalTwin.plant_id),
          survey_id: String(latest3DMeshDigitalTwin.survey_id),
          method_id: String(latest3DMeshDigitalTwin.method_id),
        });
        const response = await fetch(
          buildDigitalTwinDataUrl(`digital_twin/?${params.toString()}`),
          {
            headers: getDigitalTwinDataHeaders(),
          },
        );

        if (!isMounted) return;

        if (!response.ok) {
          throw new Error(`Errore download modello 3D: ${response.status}`);
        }

        const jsonPayload = await response.json();
        const modelData = createDigitalTwinModelObjectUrlFromBase64(
          getDigitalTwinModelBase64Payload(jsonPayload),
          latest3DMeshDigitalTwin.model_file,
        );

        setModelPayloadFormat(modelData?.format || null);

        if (modelData && modelData.isCompatible === false) {
          console.warn(`Formato modello 3D non compatibile con GLTFLoader: ${modelData.format}`);
          setModelStatus("incompatible");
          return;
        }

        if (!modelData?.url || !modelData?.isCompatible) {
          throw new Error("La risposta JSON del modello 3D non contiene un model_base64 valido");
        }

        objectUrlToRevoke = modelData.shouldRevoke ? modelData.url : null;
        setModelUrl(modelData.url);
        setModelStatus("ready");
      } catch (downloadError) {
        console.error("Errore durante il fetch del modello 3D:", downloadError);
        if (isMounted) {
          setModelUrl(null);
          setModelStatus("error");
        }
      }
    };

    loadDigitalTwinModel();

    return () => {
      isMounted = false;
      if (objectUrlToRevoke) {
        window.URL.revokeObjectURL(objectUrlToRevoke);
      }
    };
  }, [latest3DMeshDigitalTwin]);

  const mockupInfos = [
    { time: "10:00", value: "Lorem ipsum dolor sit amet" },
    { time: "11:00", value: "Consectetur adipiscing elit" },
    {
      time: "12:00",
      value:
        "Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua",
    },
  ];

  return (
    <Box sx={{ p: 2, pt: 4 }}>
      {/* cards */}
      <Grid container spacing={2}>
        <Grid item xs={8}>
          {/* This component provides the card that shows the 3D model of the selected plant. We can move the model, zoom in and zoom out. */}
          <ThreeDCard
            modelUrl={modelUrl}
            modelStatus={modelStatus}
            modelTimestamp={latest3DMeshDigitalTwin?.survey_timestamp}
            modelUpdatedHoursAgo={latest3DMeshUpdatedHoursAgo}
            modelPayloadFormat={modelPayloadFormat}
          />
          {/* This card shows the description of some events regarding the selected plant */}
          <TimelineCard title="Timeline Eventi" infos={mockupInfos} />
          <MeasurementTrendCards
            measurements={plantsMeasurementsData}
            measurementTypes={TREND_MEASUREMENTS}
            openTrendKey={openTrendKey}
            onToggle={setOpenTrendKey}
          />
        </Grid>
        <Grid item xs={4}>
          {/* We provide some cards that are going to show important informations, like suggestions, warnings, */}
          {/* <SuggestionCard
            title="Info"
            description="This is an info card"
            type="alert"
          /> */}
          {/* plant score and some indicators of the plant's health state. */}
          {/* All components are reusable in different ways and they can be adapted. */}
          <SingleInfoCard
            title="Stato salute"
            value="-"
            valueColor="#900B09"
            subtitle="Score generale dello stato di salute"
          />
          <SingleInfoCard
            title="NDVI"
            value={findNDVI()}
            subtitle="Indice vegetazione"
          />
          {/* Plant's data is important because contains some basic infos. Data is shown like a key/data pairing with a mapping that show a consistent label. */}
          <PlantInfosCard
            title="Caratteristiche"
            infos={plantsMeasurementsData}
          />
        </Grid>
      </Grid>
      {/* For every page, we have a classic snackbar that shows the errors. */}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        message={error}
      />
    </Box>
  );
};

export default CropDetailPage;
